import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import QrScanner from 'qr-scanner';
import Button from '../../atoms/Button';
import MaskedInput from '../../atoms/MaskedInput';
import Card from '../../atoms/Card';
import { OrderOut } from '../../../types/api';
import { apiService } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import './BartenderQRScanner.scss';
import Icon from '@/components/atoms/Icon/Icon';

interface QRScannerProps {
  onOrderScanned: (order: OrderOut) => void;
  onError?: (error: string) => void;
  className?: string;
}

const BartenderQRScanner: React.FC<QRScannerProps> = ({
  onOrderScanned,
  onError,
  className = ''
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [qrInput, setQrInput] = useState('');
  const [isScanning, setIsScanning] = useState(false);
  const [scanMode, setScanMode] = useState<'camera' | 'manual'>('camera');
  const [cameraError, setCameraError] = useState<string | null>(null);
  // Mirrors the real camera: only 'starting' shows the "Iniciando cámara" overlay.
  const [cameraStatus, setCameraStatus] = useState<'starting' | 'active' | 'error'>('starting');

  // QR Scanner refs
  const scanner = useRef<QrScanner | null>(null);
  const videoEl = useRef<HTMLVideoElement>(null);
  const qrBoxEl = useRef<HTMLDivElement>(null);
  // The camera keeps running while a code is processed (#177): detections are
  // ignored while busy, and a code that just failed is ignored for the
  // backend's 30s per-code cooldown so the same QR held in front of the camera
  // doesn't hammer the API (and pile up scans that could lock the order).
  const busyRef = useRef(false);
  const lastFailedRef = useRef<{ code: string; at: number } | null>(null);
  const FAILED_CODE_COOLDOWN_MS = 30000;

  const processQRCode = async (qrCode: string) => {
    const processedCode = qrCode.trim();
    if (!processedCode) {
      onError?.(t('bartender.qr.enterQRCode'));
      return;
    }
    if (busyRef.current) return;

    busyRef.current = true;
    setIsScanning(true);
    let failed = true;
    try {
      // Single call - get order directly (validation, including "recently
      // used", is enforced by the backend)
      const order = await apiService.scanOrderByQr(processedCode);

      // Validate that the order belongs to the bartender's event
      if (user?.event_id && order.event_id && user.event_id !== order.event_id) {
        onError?.(t('bartender.qr.wrongEvent'));
        return;
      }

      failed = false;
      setQrInput(''); // Clear input after successful scan
      onOrderScanned(order);
    } catch (error: any) {
      console.error('QR scan error:', error);
      if (error.response?.status === 404) {
        onError?.(t('bartender.qr.orderNotFound'));
      } else if (error.response?.status === 429) {
        onError?.(t('bartender.qr.codeTooNew'));
      } else if (error.response?.data?.detail) {
        // Show the specific backend error message
        onError?.(error.response.data.detail);
      } else if (error.response?.data?.message) {
        onError?.(error.response.data.message);
      } else {
        onError?.(t('bartender.qr.scanError'));
      }
    } finally {
      if (failed) {
        lastFailedRef.current = { code: processedCode, at: Date.now() };
      }
      busyRef.current = false;
      setIsScanning(false);
    }
  };

  // The scanner instance keeps the callback it was created with, so route
  // detections through a ref to always use the latest processQRCode.
  const processRef = useRef(processQRCode);
  processRef.current = processQRCode;

  const handleManualScan = async () => {
    lastFailedRef.current = null; // an explicit tap always retries
    await processQRCode(qrInput);
  };

  // QR Scanner success handler
  const onScanSuccess = (result: QrScanner.ScanResult) => {
    const code = result?.data?.trim();
    if (!code || busyRef.current) return;
    const lastFailed = lastFailedRef.current;
    if (lastFailed && lastFailed.code === code && Date.now() - lastFailed.at < FAILED_CODE_COOLDOWN_MS) {
      return;
    }
    processRef.current(code);
  };

  // QR Scanner error handler
  const onScanFail = (err: string | Error) => {
    const errorMessage = err?.toString() || '';
    
    // Ignore "No QR code found" errors - these are normal during scanning
    if (errorMessage.includes('No QR code found') || 
        errorMessage.includes('QR code not found') ||
        errorMessage.includes('No QR code detected')) {
      return; // Don't log or handle these normal scanning errors
    }
    
    // Only handle critical camera access errors, not QR decoding errors
    if (errorMessage.includes('Permission') || 
        errorMessage.includes('NotAllowed') || 
        errorMessage.includes('NotFound') ||
        errorMessage.includes('camera') ||
        errorMessage.includes('getUserMedia') ||
        errorMessage.includes('NotSupportedError') ||
        errorMessage.includes('NotReadableError')) {
      console.error('Camera access error:', err);
      
      // Check if it's an HTTPS issue
      if (window.location.protocol === 'http:' && window.location.hostname !== 'localhost') {
        setCameraError(t('bartender.qr.httpsRequired'));
      } else {
        setCameraError(t('bartender.qr.cameraError'));
      }
      
      setCameraStatus('error');
      setScanMode('manual');
    } else {
      // Log other errors but don't spam the console
      console.log('QR Scan error (non-critical):', err);
    }
  };

  // Start the camera when entering camera mode; release it when leaving the
  // mode or closing the scanner. Start and teardown are chained so a new
  // scanner never grabs the <video> while the previous one is still starting
  // or stopping (double-mounts and quick mode switches left it blank).
  const teardownRef = useRef<Promise<void>>(Promise.resolve());

  useEffect(() => {
    if (scanMode !== 'camera') return;

    let cancelled = false;
    let instance: QrScanner | null = null;
    setCameraStatus('starting');

    const started = (async () => {
      await teardownRef.current;
      if (cancelled || !videoEl.current) return;

      instance = new QrScanner(videoEl.current, onScanSuccess, {
        onDecodeError: onScanFail,
        preferredCamera: 'environment', // Use back camera
        highlightScanRegion: true,
        highlightCodeOutline: true,
        overlay: qrBoxEl?.current || undefined,
      });
      scanner.current = instance;

      try {
        await instance.start();
        if (cancelled) return;
        setCameraStatus('active');
        setCameraError(null);
      } catch (err: any) {
        if (cancelled) return;
        console.error('Failed to start QR scanner:', err);
        setCameraError(t('bartender.qr.cameraError'));
        setCameraStatus('error');
      }
    })();

    return () => {
      cancelled = true;
      teardownRef.current = started
        .catch(() => undefined)
        .then(() => {
          if (!instance) return;
          if (scanner.current === instance) scanner.current = null;
          instance.destroy();
        });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanMode]);

  const handleModeSwitch = (mode: 'camera' | 'manual') => {
    // Tapping the mode that is already active must not restart anything (#176)
    if (mode === scanMode) return;
    setScanMode(mode);
    setCameraError(null);
  };


  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleManualScan();
    }
  };

  return (
    <Card className={`bartender-bartender-qr-scanner ${className}`}>
      <div className="bartender-qr-scanner__header">
        <h3 className="bartender-qr-scanner__title">
          <Icon name="qr" size={20} alt={t('bartender.qr.title')} /> {t('bartender.qr.title')}
        </h3>
        
      </div>

      <div className="bartender-qr-scanner__mode-switcher">
        <Button
          onClick={() => handleModeSwitch('camera')}
          variant={scanMode === 'camera' ? 'primary' : 'ghost'}
          size="sm"
          className="bartender-qr-scanner__mode-btn"
        >
          <Icon name="camara" size={20} alt={t('bartender.qr.cameraMode')} /> {t('bartender.qr.cameraMode')}
        </Button>
        <Button
          onClick={() => handleModeSwitch('manual')}
          variant={scanMode === 'manual' ? 'primary' : 'ghost'}
          size="sm"
          className="bartender-qr-scanner__mode-btn"
        >
          <Icon name="teclado" size={20} alt={t('bartender.qr.manualMode')} /> {t('bartender.qr.manualMode')}
        </Button>
      </div>

      {cameraError && (
        <div className="bartender-qr-scanner__camera-error">
          ⚠️ {cameraError}
        </div>
      )}

      {scanMode === 'camera' ? (
        <div className="bartender-qr-scanner__camera-section">
          <div className="bartender-qr-scanner__camera-container">
            {cameraStatus === 'starting' && (
              <div className="bartender-qr-scanner__camera-loading">
                📷 {t('bartender.qr.cameraLoading')}
              </div>
            )}
            <video ref={videoEl} className="bartender-qr-scanner__camera"></video>
            <div ref={qrBoxEl} className="bartender-qr-scanner__qr-box">
              {/* QR frame overlay */}
            </div>
          </div>
          <div className="bartender-qr-scanner__camera-note">
            {cameraStatus === 'active' ? `📱 ${t('bartender.qr.cameraReady')}` : t('bartender.qr.cameraNote')}
            {window.location.protocol === 'http:' && window.location.hostname !== 'localhost' && (
              <div className="bartender-qr-scanner__https-warning">
                ⚠️ {t('bartender.qr.httpsWarning')}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="bartender-qr-scanner__input-section">
          <div className="bartender-qr-scanner__input-group">
            <MaskedInput
              mask="AAAAAA"
              placeholder={t('bartender.qr.placeholder')}
              value={qrInput}
              onChange={(value: string) => setQrInput(value)}
              onKeyDown={handleKeyDown}
              className="bartender-qr-scanner__input"
              autoFocus
              convertCase="upper"
            />
            <Button
              onClick={handleManualScan}
              loading={isScanning}
              disabled={isScanning}
              variant="primary"
              className="bartender-qr-scanner__scan-btn"
            >
              {isScanning ? t('bartender.qr.scanning') : t('bartender.qr.scan')}
            </Button>
          </div>
          
          <div className="bartender-qr-scanner__manual-note">
            💡 {t('bartender.qr.manualNote')}
          </div>
        </div>
      )}
    </Card>
  );
};

export default BartenderQRScanner;